import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { rolesRoute, systemRolesRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ExceptionType } from 'src/app/common-components/errors/models/error-message';
import { Privileges } from 'src/app/privileges/privileges';
import { environment } from 'src/environments/environment';
import { GetPrivileges } from '../queries/generated/GetPrivileges';
import { GetRoleAccountId, GetRoleAccountIdVariables } from '../queries/generated/GetRoleAccountId';
import { GetRoleDetail, GetRoleDetailVariables } from '../queries/generated/GetRoleDetail';
import { getPrivileges, getRoleAccountId, getRoleDetail } from '../queries/role-detail.graphql';
import { RoleDetailApiService } from '../services/role-detail-api.service';
import * as RoleDetailActions from './role-detail.actions';

@Injectable()
export class RoleDetailEffects {
   loadRoleDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            RoleDetailActions.roleDetailLoad,
            RoleDetailActions.saveRoleDetailSuccess,
            RoleDetailActions.roleDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         mergeMap(([action, params]) => {
            const id = parseInt(params.id, 10);
            return this.apiService
               .query<GetRoleAccountId, GetRoleAccountIdVariables>({
                  query: getRoleAccountId,
                  variables: { id },
               })
               .pipe(
                  map((result) => result.data.role.accountId),
                  catchError((error) => {
                     return of(RoleDetailActions.loadRoleDetailsFailure(error));
                  })
               );
         }),
         withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
         concatMap(([accountId, params, route]) => {
            const path = route.url[0].path;
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetRoleDetail, GetRoleDetailVariables>({
                  query: getRoleDetail,
                  variables: {
                     id,
                     extensionFilter: `accountId = ${accountId ?? environment.lcsAccountId}`,
                     extensionGroupFilter: `accountId = ${accountId ?? environment.lcsAccountId}`,
                  },
               })
               .pipe(
                  withLatestFrom(this.store.pipe(select(selectPrivileges))),
                  map(([roleResult, privileges]) => {
                     const role = roleResult.data.role;

                     const isSystemRole = !role.account?.accountId;

                     const expectedRoute = isSystemRole ? systemRolesRoute : rolesRoute;

                     if (path !== expectedRoute) {
                        this.router.navigate([expectedRoute, id], {
                           replaceUrl: true,
                        });
                        return RoleDetailActions.roleRouteCorrected();
                     }

                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageRoles_View,
                           roleResult.data.role.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return RoleDetailActions.roleDetailAccessDenied();
                     }
                     if (path === systemRolesRoute) {
                        return RoleDetailActions.loadSystemRoleDetailsSuccess({
                           data: roleResult.data.role,
                           extensions: roleResult.data.extensionsConnection,
                           extensionGroups: roleResult.data.extensionGroupsConnection,
                        });
                     } else {
                        return RoleDetailActions.loadRoleDetailsSuccess({
                           data: roleResult.data.role,
                           extensions: roleResult.data.extensionsConnection,
                           extensionGroups: roleResult.data.extensionGroupsConnection,
                        });
                     }
                  }),
                  catchError((error) => {
                     return of(RoleDetailActions.loadRoleDetailsFailure(error));
                  })
               );
         })
      );
   });

   loadPrivileges = createEffect(() => {
      return this.actions$.pipe(
         ofType(RoleDetailActions.privilegesLoad, RoleDetailActions.roleDetailCancel),
         withLatestFrom(this.store.pipe(select(selectPrivileges))),
         switchMap(([action, privileges]) => {
            if (true) {
               return this.apiService.query<GetPrivileges, {}>({ query: getPrivileges }).pipe(
                  map((result) => {
                     return RoleDetailActions.privilegesLoadSuccess({ data: result.data.privilegesConnection.items });
                  }),
                  catchError((error) => {
                     return of(RoleDetailActions.privilegesLoadFailure(error));
                  })
               );
            }
            return EMPTY;
         })
      );
   });

   saveRoleDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(RoleDetailActions.saveRoleDetail),
         switchMap((action) => {
            const apiCall = this.roleDetailApiService.save(
               action.role,
               action.roleFields,
               action.rolePrivilegeFields,
               action.roleCallRecordingExtensionFields,
               action.roleCallRecordingExtensionGroupFields
            );
            return apiCall.pipe(
               map((result) => {
                  return RoleDetailActions.saveRoleDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(RoleDetailActions.saveRoleDetailFailure(error));
               })
            );
         })
      );
   });

   saveRoleDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(RoleDetailActions.saveRoleDetailSuccess),
            tap(() => {
               this.snackBar.open('Role Saved');
               return this.store.dispatch(RoleDetailActions.saveRoleDetailSuccessForNotify());
            })
         );
      },
      {
         dispatch: false,
      }
   );

   deleteRole = createEffect(() => {
      return this.actions$.pipe(
         ofType(RoleDetailActions.deleteRole),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Role?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Role?',
                  message: message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.roleDetailApiService.deleteRole(action.roleId).pipe(
                        map((value) => {
                           if (value.errors) {
                              return RoleDetailActions.deleteRoleFailure({
                                 error: value,
                                 exceptionType: ExceptionType.Graph,
                                 status: 0,
                              });
                           }
                           const isOnSystemRolePage = this.router.url.includes(systemRolesRoute);
                           return RoleDetailActions.deleteRoleSuccess({
                              name: action.name,
                              isOnSystemPage: isOnSystemRolePage,
                           });
                        }),
                        catchError((error) => {
                           return of(RoleDetailActions.deleteRoleFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteRoleSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(RoleDetailActions.deleteRoleSuccess),
            tap((action) => {
               if (action.isOnSystemPage) this.router.navigate([systemRolesRoute]);
               else this.router.navigate([rolesRoute]);
               this.snackBar.open(`Role '${action.name}' deleted`);
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private roleDetailApiService: RoleDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location
   ) {}
}
